// live/webAudio.ts — the live call on WEB and macOS, using the browser's own audio stack.
//
// Same four functions as the iOS native plugin (start / play / flush / stop) and the same MicFrame
// shape, so `useLiveSession` picks a path and nothing above it changes.
//
// WHY THE BROWSER IS THE RIGHT ANSWER HERE (and not on iOS): every voice assistant on the web —
// ChatGPT, Claude, Gemini — runs exactly this stack. `getUserMedia` gives us the platform's echo
// canceller for free, on every OS, with no plugin to build or ship. The native plugin stays on iOS
// because that is where webview AEC genuinely was unreliable; on desktop it is what everyone uses.
//
// THE TWO THINGS THAT ACTUALLY MATTER:
//
// 1. RATE. The vendor wants PCM16 mono at 16 kHz in, and sends 24 kHz back. Browsers capture at the
//    hardware rate (usually 48 kHz), so we downsample on the way in and play back at the rate the
//    audio actually is. Getting either wrong yields chipmunk or slow-motion speech, not an error.
//
// 2. ECHO. `echoCancellation` handles the speaker→mic loop, but it is not enough on its own: while
//    the model talks we ALSO stop sending mic audio, and resume shortly after it stops. Without that
//    gate the model hears itself, interrupts itself, and the call falls apart on laptop speakers.

export interface MicFrame { data: string; rms: number }

const IN_RATE = 16000;   // what the vendor expects from us
const FRAME = 1600;      // 100ms @ 16k — matches the native plugin's cadence

let ctx: AudioContext | null = null;
let stream: MediaStream | null = null;
let node: ScriptProcessorNode | null = null;
let source: MediaStreamAudioSourceNode | null = null;

// Playback is a scheduled queue: each chunk is placed exactly after the previous one, so speech
// comes out continuous. Playing chunks "now" as they arrive produces audible seams.
let playCtx: AudioContext | null = null;
let playHead = 0;
let speaking = false;          // model currently talking → hold the mic
let unmuteAt = 0;              // …and for a moment after, to catch the tail through the speakers

const b64 = (bytes: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};

/** Start capture. Resolves once the mic is live; `onFrame` then fires ~10×/second. */
export async function webStart(onFrame: (f: MicFrame) => void): Promise<{ hwRate: number; micPerm: string }> {
  stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,   // the platform's AEC — the reason this works at all
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
  });

  ctx = new AudioContext();
  const hwRate = ctx.sampleRate;                 // 48000 on nearly every machine
  source = ctx.createMediaStreamSource(stream);

  // ScriptProcessor is deprecated in favour of AudioWorklet, but a worklet needs a separate module
  // file fetched at runtime — which the Tauri webview serves from a different origin than the page
  // in some builds. This runs everywhere we ship today with no extra asset. Revisit if it ever
  // becomes a CPU problem; at 100ms frames it is not.
  node = ctx.createScriptProcessor(4096, 1, 1);
  let carry: number[] = [];

  node.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);

    // Downsample hwRate → 16k by linear interpolation. Nearest-neighbour is cheaper and sounds
    // noticeably worse to a speech model.
    const ratio = hwRate / IN_RATE;
    for (let i = 0; i < input.length / ratio; i++) {
      const pos = i * ratio;
      const lo = Math.floor(pos);
      const frac = pos - lo;
      const a = input[lo] ?? 0;
      const b = input[lo + 1] ?? a;
      carry.push(a + (b - a) * frac);
    }

    while (carry.length >= FRAME) {
      const chunk = carry.slice(0, FRAME);
      carry = carry.slice(FRAME);

      let sum = 0;
      const pcm = new Uint8Array(FRAME * 2);
      const view = new DataView(pcm.buffer);
      for (let i = 0; i < FRAME; i++) {
        const s = Math.max(-1, Math.min(1, chunk[i]));
        sum += s * s;
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);  // little-endian PCM16
      }
      const rms = Math.sqrt(sum / FRAME);

      // THE GATE. Drop mic audio while the model speaks, and briefly after — the speaker tail
      // reaches the mic after the stream ends. RMS still reports so the UI meter stays honest.
      if (speaking || performance.now() < unmuteAt) {
        onFrame({ data: "", rms });
        continue;
      }
      onFrame({ data: b64(pcm), rms });
    }
  };

  source.connect(node);
  // Safari will not run a ScriptProcessor that is not connected to a destination. Route it into a
  // silent gain node so nothing is actually audible.
  const sink = ctx.createGain();
  sink.gain.value = 0;
  node.connect(sink);
  sink.connect(ctx.destination);

  return { hwRate, micPerm: "granted" };
}

/** Play one base64 PCM16 chunk from the model. `rate` is the vendor's output rate. */
export function webPlay(dataB64: string, rate = 24000): void {
  if (!playCtx) playCtx = new AudioContext();
  const bin = atob(dataB64);
  const samples = bin.length / 2;
  if (!samples) return;

  const buf = playCtx.createBuffer(1, samples, rate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < samples; i++) {
    const v = (bin.charCodeAt(i * 2) | (bin.charCodeAt(i * 2 + 1) << 8)) << 16 >> 16;  // LE → signed
    ch[i] = v / 0x8000;
  }

  const src = playCtx.createBufferSource();
  src.buffer = buf;
  src.connect(playCtx.destination);

  const now = playCtx.currentTime;
  if (playHead < now) playHead = now;          // fell behind (first chunk, or a gap) → start now
  src.start(playHead);
  playHead += buf.duration;

  speaking = true;
  src.onended = () => {
    // Only the LAST scheduled chunk should re-open the mic.
    if (playCtx && playHead - playCtx.currentTime < 0.05) {
      speaking = false;
      unmuteAt = performance.now() + 300;      // let the room's tail die down first
    }
  };
}

/** Barge-in: the customer started talking, so drop everything still queued. */
export function webFlush(): void {
  if (playCtx) {
    // Closing and reopening is the only reliable way to cancel already-scheduled sources.
    void playCtx.close();
    playCtx = null;
  }
  playHead = 0;
  speaking = false;
  unmuteAt = 0;
}

/** Stop capture and release the microphone (the browser's recording indicator must go away). */
export function webStop(): void {
  try { node?.disconnect(); } catch { /* already torn down */ }
  try { source?.disconnect(); } catch { /* already torn down */ }
  stream?.getTracks().forEach((t) => t.stop());
  void ctx?.close();
  ctx = null; node = null; source = null; stream = null;
  webFlush();
}
